import { useState, type ReactNode } from 'react'
import { FeatherIcon } from '../../components/FeatherIcon'
import { errorMessage } from '../../chat/api'
import { newKey } from '../statements/statementsData'
import { useStats, useStatsT } from './statsContext'
import { Spark } from './StatsChart'
import { Card, CardEmpty, Delta, ErrorLine, Kv, Loading, MediaCount, Pills, ReadMore, SummaryGrid, SummaryItem, Thumb, UserCell } from './StatsParts'
import {
  dig,
  earningsChartQuery,
  fanSums,
  formatDate,
  formatDateTime,
  int,
  minPayout,
  money,
  PILLS,
  plain,
  referralUrl,
  SIDE_EARNING_TYPES,
  streamStats,
  tipsText,
  withRange,
  type Raw,
} from './statisticsData'
import { combined, statKey, useStat, useStatAll } from './statsStore'

// ── Statements column ────────────────────────────────────────────────────

export function TopBanner() {
  const { t } = useStatsT()
  const { store } = useStats()
  const me = useStat(store, 'users/me')
  const top = Number(me.data?.performerTop)
  if (me.status !== 'ok' || !(top > 0)) return null
  return (
    <div className="ofx-top-banner">
      <FeatherIcon name="star" size={16} />
      <span>{t('topCreators', { n: top })}</span>
    </div>
  )
}

/** Current / pending balance, payout frequency, and REQUEST WITHDRAWAL (the Statements page's rules). */
export function BalanceCard({ extraTop }: { extraTop?: ReactNode }) {
  const { t } = useStatsT()
  const { store, request, say, openWithdrawal, canWritePayouts } = useStats()
  const balances = useStat(store, 'payouts/balances')
  const receive = useStat(store, 'payouts/check-receive')
  const [saving, setSaving] = useState(false)
  if (balances.status === 'loading')
    return (
      <Card className="ofx-balance-card">
        {extraTop}
        <Loading />
      </Card>
    )
  if (balances.status === 'error')
    return (
      <Card className="ofx-balance-card">
        {extraTop}
        <ErrorLine message={balances.error} />
      </Card>
    )
  const b = balances.data ?? {}
  const min = minPayout(b)
  const period = String(b.withdrawalPeriod || '')
  const options = Array.isArray(b.withdrawalPeriodOptions) ? (b.withdrawalPeriodOptions as Raw[]) : []
  const manual = period === 'manual'
  const cannotReceive = receive.data?.canReceiveManualPayout === false
  const belowMin = (Number(b.payoutAvailable) || 0) < min
  const pendingDays = Number(b.manualPayoutPendingDays)
  const pendingTip = pendingDays > 0 ? t('pendingTip', { n: pendingDays }) : t('pendingInfo')
  const hint = t(manual ? 'minManual' : period === 'weekly' ? 'autoWeekly' : period === 'monthly' ? 'autoMonthly' : 'autoOther', { min: money(min, 0) })
  const key = statKey('payouts/balances')

  const change = async (code: string) => {
    if (!canWritePayouts || !code || code === period || saving) return
    setSaving(true)
    store.patch(key, { ...b, withdrawalPeriod: code })
    let failure = ''
    try {
      const { status, data } = await request('/v1/payouts/requests/period', { body: { withdrawal_period: code, idempotency_key: newKey('period') } })
      if (status >= 400 || (data as Raw | null)?.ok === false) failure = errorMessage(data, `HTTP ${status}`)
    } catch (e) {
      failure = e instanceof Error ? e.message : 'Request failed'
    }
    if (failure) {
      const now = (store.get(key)?.data ?? b) as Raw
      store.patch(key, { ...now, withdrawalPeriod: period })
      say(t('periodFailed', { error: failure }))
    } else {
      say(t('periodUpdated'))
    }
    setSaving(false)
  }

  return (
    <Card className="ofx-balance-card">
      {extraTop}
      <div className="ofx-balances">
        <div>
          <strong>{money(b.payoutAvailable)}</strong>
          <span>{t('currentBalance')}</span>
        </div>
        <div className="is-muted">
          <strong>{money(b.payoutPending)}</strong>
          <span>
            {t('pendingBalance')}
            <span className="ofx-info-tip" tabIndex={0} title={pendingTip} aria-label={pendingTip}>
              <FeatherIcon name="info" size={14} />
            </span>
          </span>
        </div>
      </div>
      <div className="ofx-payout">
        <label className="ofx-payout-select">
          <select
            className="ofx-select"
            value={period}
            disabled={!options.length || saving || !canWritePayouts}
            aria-label={t('payoutFrequency')}
            onChange={(e) => void change(e.target.value)}
          >
            {options.map((o) => (
              <option key={String(o.code)} value={String(o.code)}>
                {String(o.name || o.code)}
              </option>
            ))}
          </select>
          <small>{hint}</small>
        </label>
        {canWritePayouts && manual && (
          <button
            type="button"
            className="ofx-request-btn"
            disabled={cannotReceive || belowMin}
            title={cannotReceive ? t('cannotReceive') : belowMin ? t('belowMin', { min: money(min, 0) }) : ''}
            onClick={openWithdrawal}
          >
            {t('requestWithdrawal')}
          </button>
        )}
      </div>
    </Card>
  )
}

/** Statements' right column "Earnings": one row per category with money (and calls). */
export function EarningsSide() {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const all = useStatAll(store, SIDE_EARNING_TYPES.map((type) => ['earnings/chart', earningsChartQuery(range, type.filter)]))
  const entries = SIDE_EARNING_TYPES.map((type, i) => [type, all[i]!] as const)
  const state = combined(...all)
  const title = t('l.earnings')
  if (state.loading)
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (state.error)
    return (
      <Card title={title}>
        <ErrorLine message={state.error} />
      </Card>
    )
  return (
    <Card title={title} className="ofx-earnings-side">
      {entries
        .map(([type, e]) => ({ type, d: (e.data?.[type.key] ?? {}) as Raw }))
        .filter(({ type, d }) => type.id === 'total' || Number(d.total) > 0)
        .map(({ type, d }) => (
          <div key={type.id} className="ofx-side-row">
            <div>
              <span>{t(`l.${type.side}`)}</span>
              <strong>
                {money(d.total)}
                <Delta value={d.delta} signed />
              </strong>
            </div>
            <Spark points={d.chartAmount} tone={type.tone} />
          </div>
        ))}
    </Card>
  )
}

export function ChargebackRatio() {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, 'payouts/chargebacks/ratio', withRange(range))
  if (entry.status !== 'ok') return null
  const ratio = Number(entry.data?.chargebacksRatio)
  return (
    <div className="ofx-ratio">
      <FeatherIcon name="info" size={18} />
      <span>{t('chargebackRatio', { n: Number.isFinite(ratio) ? Number(ratio.toFixed(2)) : '—' })}</span>
    </div>
  )
}

export function ReferralCard() {
  const { t } = useStatsT()
  const { store, say } = useStats()
  const balance = useStat(store, 'payments/referrals/balance')
  const me = useStat(store, 'users/me')
  const balances = useStat(store, 'payouts/balances')
  const url = referralUrl(me.data?.id)
  const earnings = balance.status === 'ok' ? money(balance.data?.referralEarnings) : balance.status === 'loading' ? '…' : '—'
  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      say(t('referralCopied'))
    } catch {
      say(url)
    }
  }
  return (
    <Card className="ofx-referral-card">
      <div className="ofx-referral">
        <strong>{earnings}</strong>
        <span>{t('currentReferralEarnings')}</span>
        <small>{t('referralNote', { min: money(minPayout(balances.data), 0) })}</small>
      </div>
      <div className="ofx-referral-url">
        <div>{url || '—'}</div>
        <small>{t('referralUrl')}</small>
        <button type="button" className="ofx-link-btn" disabled={!url} onClick={() => void copy()}>
          {t('copyLink')}
        </button>
      </div>
    </Card>
  )
}

// ── Summary (users/me/stats/overview?by=…) ───────────────────────────────

export function SummaryCard({ by, empty, children }: { by: string; empty?: string; children: (d: Raw) => ReactNode }) {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, 'users/me/stats/overview', withRange(range, { by }))
  const title = t('summary')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const body = children(entry.data ?? {})
  return <Card title={title}>{body || <CardEmpty text={empty ?? t('empty.data')} />}</Card>
}

/** Overview's right column card: total earnings and one block per kind of activity. */
export function OverviewEarningsCard() {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, 'users/me/stats/overview', withRange(range))
  const title = t('l.earnings')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const d = entry.data ?? {}
  const sub = dig
  const v = sub(d, 'visitors')
  const p = sub(d, 'posts')
  const m = sub(d, 'massMessages')
  const s = sub(d, 'streams')
  const c = sub(d, 'calls')
  return (
    <Card title={title} className="ofx-overview-side">
      <div className="ofx-overview-total">
        <strong>{money(sub(d, 'earning').total)}</strong>
        <Delta value={sub(d, 'earning').delta} />
      </div>
      {Boolean(v.hasStatistic) && (
        <SummaryGrid spark={v.chartData} tone="s1">
          <SummaryItem label={t('l.profileVisitors')} value={int(sub(v, 'visitors').total)} change={sub(v, 'visitors').delta} />
          <SummaryItem label={t('l.subscriptionsEarnings')} value={money(sub(v, 'earnings').total)} change={sub(v, 'earnings').delta} />
          <SummaryItem
            label={t('l.newSubsRenews')}
            value={`${int(sub(v, 'subscriptions', 'new').total)} / ${int(sub(v, 'subscriptions', 'renew').total)}`}
            change={sub(v, 'subscriptions', 'new').delta}
          />
        </SummaryGrid>
      )}
      {Boolean(p.hasStatistic) && (
        <SummaryGrid spark={p.chartData} tone="s3">
          <SummaryItem label={t('l.posts')} value={int(sub(p, 'count').total)} change={sub(p, 'count').delta} />
          <SummaryItem label={t('l.postsEarnings')} value={money(sub(p, 'earnings').total)} change={sub(p, 'earnings').delta} />
          <SummaryItem label={t('l.postsViews')} value={int(sub(p, 'views').total)} change={sub(p, 'views').delta} />
        </SummaryGrid>
      )}
      {Boolean(m.hasStatistic) && (
        <SummaryGrid spark={m.chartData} tone="s4">
          <SummaryItem label={t('l.messages')} value={int(sub(m, 'count').total)} change={sub(m, 'count').delta} />
          <SummaryItem label={t('l.messagesEarnings')} value={money(sub(m, 'earnings').total)} change={sub(m, 'earnings').delta} />
        </SummaryGrid>
      )}
      {Boolean(s.hasStatistic) && (
        <SummaryGrid spark={s.chartData} tone="s6">
          <SummaryItem label={t('l.streams')} value={String(sub(s, 'count').total ?? '0m')} change={sub(s, 'count').delta} />
          <SummaryItem label={t('l.streamsEarnings')} value={money(sub(s, 'earnings').total)} change={sub(s, 'earnings').delta} />
        </SummaryGrid>
      )}
      {Boolean(c.hasStatistic) && (
        <SummaryGrid spark={c.chartData} tone="s5">
          <SummaryItem label={t('l.callsEarnings')} value={money(sub(c, 'earnings').total)} change={sub(c, 'earnings').delta} />
        </SummaryGrid>
      )}
    </Card>
  )
}

// ── Top items ────────────────────────────────────────────────────────────

function Locked({ price }: { price: unknown }) {
  if (!(Number(price) > 0)) return null
  return (
    <span className="ofx-muted ofx-price">
      {money(price)} <FeatherIcon name="lock" size={13} />
    </span>
  )
}

function TopPostItem({ post, pill }: { post: Raw; pill: string }) {
  const { t, intl } = useStatsT()
  const s = (post.stats ?? {}) as Raw
  return (
    <div className="ofx-top-item">
      <div className="ofx-top-item-head">
        <strong>{formatDateTime(post.postedAt, intl)}</strong>
        <Locked price={post.price} />
      </div>
      <div className="ofx-top-item-body">
        <Thumb media={post.media} />
        <div className="ofx-content-text">
          <ReadMore id={`top-post-${pill}-${post.id}`} text={String(post.rawText || plain(post.text))} />
          <MediaCount media={post.media} count={post.mediaCount} />
        </div>
      </div>
      <div className="ofx-top-stats">
        <span>
          {t('l.views')} <strong>{int(s.lookCount)}</strong>
        </span>
        <span>
          {t('l.likes')} <strong>{int(s.likeCount)}</strong>
        </span>
        <span>
          {t('l.comments')} <strong>{int(s.commentCount)}</strong>
        </span>
        <span>
          {t('l.tips')} <strong>{String(post.tipsAmount || money(s.tipSum))}</strong>
        </span>
        <span>
          {t('l.purchases')} <strong>{int(s.purchasedCount)}</strong>
        </span>
      </div>
    </div>
  )
}

/** "Top Post": the best post per metric; metrics with no post are disabled. */
export function TopPostCard({ compact = false }: { compact?: boolean }) {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const entry = useStat(store, 'users/me/stats/top/post', withRange(range, { skip_users: 'all' }))
  const title = t('topPost')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const d = entry.data ?? {}
  const available = PILLS.topPost.map(([id]) => id).filter((id) => d[id])
  if (!available.length)
    return (
      <Card title={title}>
        <CardEmpty text={t('empty.posts')} />
      </Card>
    )
  const pill = available.includes(pills.topPost) ? pills.topPost : (available[0] ?? '')
  return (
    <Card title={title} className={`ofx-top-card ${compact ? 'is-compact' : ''}`}>
      <Pills items={PILLS.topPost} active={pill} small onPick={(id) => setPill('topPost', id)} disabled={(id) => !d[id]} />
      <TopPostItem post={d[pill] as Raw} pill={pill} />
    </Card>
  )
}

function MessageItem({ msg }: { msg: Raw }) {
  const { t, intl } = useStatsT()
  return (
    <div className="ofx-top-item">
      <div className="ofx-top-item-head">
        <strong>{formatDateTime(msg.date || msg.createdAt, intl)}</strong>
        <Locked price={msg.price} />
      </div>
      <div className="ofx-top-item-body">
        <Thumb media={msg.media} />
        <div className="ofx-content-text">
          <ReadMore id={`msg-${msg.id}`} text={String(msg.rawText || plain(msg.text))} />
          <MediaCount media={msg.media} count={msg.mediaCount} />
        </div>
      </div>
      <div className="ofx-top-stats">
        <span>{t('purchasesViews', { money: money((Number(msg.price) || 0) * (Number(msg.purchasedCount) || 0)), n: int(msg.viewedCount) })}</span>
      </div>
    </div>
  )
}

export function TopMessageCard() {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, 'users/me/stats/top/message', withRange(range))
  const title = t('topMessage')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const msg = entry.data?.purchases as Raw | undefined
  if (!msg)
    return (
      <Card title={title}>
        <CardEmpty text={t('empty.messages')} />
      </Card>
    )
  return (
    <Card title={title} className="ofx-top-card">
      <Pills items={[['purchases', 'purchases']]} active="purchases" small />
      <MessageItem msg={msg} />
    </Card>
  )
}

export function StreamRow({ stream }: { stream: Raw }) {
  const { t, intl } = useStatsT()
  const media = stream.media ?? (stream.thumb ? [{ files: { thumb: { url: stream.thumb } } }] : [])
  return (
    <div className="ofx-row">
      <div className="ofx-top-item-head">
        <strong>{formatDateTime(stream.startedAt || stream.createdAt || stream.date, intl)}</strong>
      </div>
      <div className="ofx-content-row">
        <Thumb media={media} />
        <div className="ofx-content-text">
          <ReadMore id={`stream-${stream.id}`} text={String(stream.description || stream.title || plain(stream.text))} />
        </div>
      </div>
      <div className="ofx-top-stats">
        {streamStats(stream).map(([k, v]) => (
          <span key={k}>
            {t(`l.${k}`)} <strong>{v}</strong>
          </span>
        ))}
      </div>
    </div>
  )
}

export function TopStreamCard() {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, 'users/me/stats/top/stream', withRange(range))
  const title = t('topStream')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const d = entry.data ?? {}
  const stream = (d.purchases || d.tips || d.views || d.likes || d.comments) as Raw | undefined
  if (!stream)
    return (
      <Card title={title}>
        <CardEmpty text={t('empty.streaming')} />
      </Card>
    )
  return (
    <Card title={title} className="ofx-top-card">
      <StreamRow stream={stream} />
    </Card>
  )
}

/** "Top Fan": compact (Top highlights) or the full card with its SUBSCRIPTION / EARNINGS tabs (Fans). */
export function TopFanCard({ full = false }: { full?: boolean }) {
  const { t, intl } = useStatsT()
  const { store, range, pills, setPill, openChat } = useStats()
  const entry = useStat(store, 'users/me/stats/top/fan', withRange(range))
  const title = t('topFan')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const d = entry.data ?? {}
  const available = PILLS.topFan.map(([id]) => id).filter((id) => d[id])
  if (!available.length)
    return (
      <Card title={title}>
        <CardEmpty text={t('empty.fans')} />
      </Card>
    )
  const pill = available.includes(pills.topFan) ? pills.topFan : (available[0] ?? '')
  const fan = d[pill] as Raw
  const sums = fanSums(fan)
  const pillRow = <Pills items={PILLS.topFan} active={pill} small onPick={(id) => setPill('topFan', id)} disabled={(id) => !d[id]} />
  if (!full) {
    return (
      <Card title={title} className="ofx-top-card">
        {pillRow}
        <div className="ofx-top-fan-row">
          <UserCell user={fan} />
          <div className="ofx-num-col">
            <strong>{money(sums.totalSumm)}</strong>
            <small>{String(sums.duration || '')}</small>
          </div>
        </div>
        <div className="ofx-total-row">
          <span>{t('l.total')}</span>
          <strong>{money(sums.totalSumm)}</strong>
        </div>
      </Card>
    )
  }
  const lists = (Array.isArray(fan.listsStates) ? (fan.listsStates as Raw[]) : []).filter((l) => l?.hasUser && l.type === 'custom').map((l) => String(l.name))
  const tab = pills.topFanTab
  const long: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  const rows: [string, string][] =
    tab === 'earnings'
      ? [
          ['subscriptions', money(sums.subscribesSumm)],
          ['tips', money(sums.tipsSumm)],
          ['messages', money(sums.messagesSumm)],
          ['posts', money(sums.postsSumm)],
          ['streams', money(sums.streamsSumm)],
          ['total', money(sums.totalSumm)],
        ]
      : [
          ['currentSubscription', Number(sums.price) > 0 ? money(sums.price) : t('free')],
          ['started', sums.subscribeAt ? formatDate(sums.subscribeAt, intl, long) : '—'],
          ['totalDuration', String(sums.duration || '—')],
          ['renews', sums.renewedAt ? formatDate(sums.renewedAt, intl, long) : '—'],
          ['rebill', t(fan.subscribedByAutoprolong === false ? 'off' : 'on')],
        ]
  return (
    <Card title={title} className="ofx-top-card">
      {pillRow}
      <div className="ofx-fan-card">
        <UserCell user={fan} />
        {lists.length > 0 && (
          <div className="ofx-fan-lists">
            <FeatherIcon name="star" size={14} />
            {t('lists')} · {lists.join(' · ')}
          </div>
        )}
        <div className="ofx-fan-actions">
          <button type="button" className="ofx-chip-btn" onClick={() => openChat(String(fan.id))}>
            <FeatherIcon name="message-square" size={14} />
            {t('message')}
          </button>
        </div>
        <div className="ofx-fan-tabs" role="tablist">
          {(['subscription', 'earnings'] as const).map((id) => (
            <button key={id} type="button" role="tab" aria-selected={id === tab} className={id === tab ? 'is-on' : ''} onClick={() => setPill('topFanTab', id)}>
              {t(`fanTab.${id}`)}
            </button>
          ))}
          <button type="button" role="tab" aria-selected={false} disabled>
            {t('fanTab.offers')}
          </button>
        </div>
        {rows.map(([k, v]) => (
          <Kv key={k} label={t(`l.${k}`)} value={v} />
        ))}
      </div>
    </Card>
  )
}

export function StoryCard({ story }: { story: Raw }) {
  const { t, intl } = useStatsT()
  return (
    <div className="ofx-story">
      <div className="ofx-top-item-head">
        <strong>{formatDateTime(story.createdAt, intl)}</strong>
      </div>
      <div className="ofx-story-body">
        <Thumb media={story.media} story />
        <div className="ofx-story-stats">
          <div>
            <span>{t('l.viewers')}</span>
            <strong className="ofx-accent">{int(story.viewersCount)}</strong>
          </div>
          <div>
            <span>{t('l.likes')}</span>
            <strong>{int(story.likesCount)}</strong>
          </div>
          <div>
            <span>{t('l.comments')}</span>
            <strong>{int(story.commentsCount)}</strong>
          </div>
          <div>
            <span>{t('l.tips')}</span>
            <strong>{`${int(story.tipsCount)} (${tipsText(story.tipsAmount, story.tipsAmountRaw)})`}</strong>
          </div>
        </div>
      </div>
    </div>
  )
}

export function TopStoryCard() {
  const { t } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const entry = useStat(store, 'users/me/stats/top/story', withRange(range))
  const title = t('topStory')
  if (entry.status === 'loading')
    return (
      <Card title={title}>
        <Loading />
      </Card>
    )
  if (entry.status === 'error')
    return (
      <Card title={title}>
        <ErrorLine message={entry.error} />
      </Card>
    )
  const d = entry.data ?? {}
  const available = PILLS.topStory.map(([id]) => id).filter((id) => d[id])
  const pill = available.includes(pills.topStory) ? pills.topStory : available[0]
  return (
    <Card title={title} className="ofx-top-card">
      <Pills items={PILLS.topStory} active={pill ?? ''} small onPick={(id) => setPill('topStory', id)} disabled={(id) => !d[id]} />
      {pill ? <StoryCard story={d[pill] as Raw} /> : <CardEmpty text={t('empty.stories')} />}
    </Card>
  )
}
